import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuthService } from '@core/services/auth.service';
import { ToastService } from '@core/services/toast.service';
import { Subscription, take } from 'rxjs';
import { matchPassword } from '@core/validators/auth.validator'
import { EncryptionService } from '@core/services/encryption.service';
import { TranslocoService } from '@jsverse/transloco';
@Component({
    selector: 'drdp-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResetPasswordComponent implements OnInit {
  private subscriptions = new Subscription();

  createPassword: boolean = false;
  modalHeader: string = '';
  resetPwForm: FormGroup | any;
  hidden: boolean = true;
  resetFailed = false;
  confirmHidden: boolean = true;
  passwordRequirements: string[] = [];

  get newPassword() { return this.resetPwForm.get('newPassword'); }
  get confirmPassword() { return this.resetPwForm.get('confirmPassword'); }
  get inputType() { return this.hidden ? "password" : "text"; }
  get confirmInputType() { return this.confirmHidden ? "password" : "text"; }


  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    private toast: ToastService,
    public dialogRef: MatDialogRef<ResetPasswordComponent>,
    @Inject(MAT_DIALOG_DATA) public userId: number,
    private encryptionService: EncryptionService,
    private translocoService: TranslocoService) {
      dialogRef.disableClose = true;
    }

  ngOnInit(): void {
    this.buildTranslatedText();
    this.resetPwForm = this.fb.group({
      newPassword: ['', [
          Validators.required,
          Validators.minLength(8),
          this.patternValidator(/\d/, { hasNumber: false }),
          this.patternValidator(/[A-Z]/, { hasCapitalCase: false }),
          this.patternValidator(/[a-z]/, { hasSmallCase: false}),
          this.patternValidator(/[!@#\$%\^&\*`~]/, { hasSpecialCharacters: false }),
        ]],
      confirmPassword: ['', [Validators.required]],
    }, { validator: matchPassword('newPassword', 'confirmPassword') });
  }

  private buildTranslatedText(): void {
    this.modalHeader = this.translocoService.translate('accounts.resetPassword.title');
    this.passwordRequirements = [
      this.translocoService.translate('accounts.resetPassword.requirements.length'),
      this.translocoService.translate('accounts.resetPassword.requirements.capitalLetter'),
      this.translocoService.translate('accounts.resetPassword.requirements.lowercaseLetter'),
      this.translocoService.translate('accounts.resetPassword.requirements.number'),
      this.translocoService.translate('accounts.resetPassword.requirements.specialCharacter'),
    ];
  }

  patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        // if control is empty return no error
        return null;
      }
      // test the value of the control against the regexp supplied
      const valid = regex.test(control.value);
      // if true, return no error (no error), else return error passed in the second parameter
      return valid ? null : error;
    };
  }

  resetConfirm(): void {
    this.subscriptions.add(this.auth.adminResetPassword(this.userId).subscribe(success => {
      if (!success) {
        this.toast.error(this.translocoService.translate('accounts.resetPassword.couldNotResetToast'))
      } else {
        this.toast.success(this.translocoService.translate('accounts.resetPassword.resetToast'))
      }
      this.dialogRef.close(success);
    }));
  }

  resetCreate(): void {
    this.createPassword = true;
    this.modalHeader = this.translocoService.translate('accounts.resetPassword.changePassword')
  }

  updatePassword(): void {
    const payload = {
      userId: this.userId,
      oldPassword: this.encryptionService.encryptPassword("Reset"),
      newPassword: this.encryptionService.encryptPassword(this.newPassword.value),
      isAdminUpdate: true,
    }

    this.auth.updatePassword(payload, this.translocoService.translate('accounts.resetPassword.updatedToast')).pipe(take(1)).subscribe((success) => {

    });
  }

  onDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
